import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, AlertCircle, ArrowRight } from "lucide-react";

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");
  const [orderId, setOrderId] = useState("");
  const [attempts, setAttempts] = useState(0);

  const pollStatus = useCallback(async () => {
    if (!sessionId || !user || user === false) return;
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      setOrderId(data.order_id || "");
      if (data.payment_status === "paid") {
        setStatus("success");
      } else if (data.status === "expired") {
        setStatus("expired");
      } else if (attempts >= 5) {
        setStatus("timeout");
      } else {
        setStatus("processing");
        setTimeout(() => setAttempts(a => a + 1), 2000);
      }
    } catch {
      if (attempts >= 5) setStatus("error");
      else setTimeout(() => setAttempts(a => a + 1), 2000);
    }
  }, [sessionId, user, attempts]);

  useEffect(() => { pollStatus(); }, [attempts, pollStatus]);

  return (
    <div data-testid="payment-success-page" className="min-h-[70vh] flex items-center justify-center py-20">
      <div className="w-full max-w-md mx-auto px-6 text-center">
        {status === "checking" || status === "processing" ? (
          <div data-testid="payment-processing">
            <Loader2 className="w-16 h-16 text-paw-primary animate-spin mx-auto mb-6" />
            <h1 className="font-outfit text-2xl font-bold text-paw-text mb-3">Processing Payment</h1>
            <p className="font-manrope text-sm text-paw-text-secondary">Please wait while we confirm your payment...</p>
          </div>
        ) : status === "success" ? (
          <div data-testid="payment-confirmed">
            <div className="w-20 h-20 rounded-full bg-paw-success/10 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-paw-success" />
            </div>
            <h1 className="font-outfit text-2xl font-bold text-paw-text mb-3">Payment Confirmed!</h1>
            <p className="font-manrope text-sm text-paw-text-secondary mb-8">
              Your order has been submitted successfully. A licensed veterinarian will review your prescription shortly.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {orderId && (
                <Link to={`/order/${orderId}`}>
                  <Button className="rounded-full bg-paw-primary hover:bg-paw-primary-hover text-white font-manrope" data-testid="view-order-btn">
                    View Order <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              )}
              <Link to="/dashboard">
                <Button variant="outline" className="rounded-full border-paw-border text-paw-text-secondary font-manrope" data-testid="go-dashboard-btn">
                  Go to Dashboard
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          <div data-testid="payment-issue">
            <div className="w-20 h-20 rounded-full bg-paw-warning/10 flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-10 h-10 text-paw-warning" />
            </div>
            <h1 className="font-outfit text-2xl font-bold text-paw-text mb-3">
              {status === "expired" ? "Session Expired" : "Checking Payment"}
            </h1>
            <p className="font-manrope text-sm text-paw-text-secondary mb-8">
              {status === "expired"
                ? "Your payment session has expired. Please try again."
                : "We're having trouble confirming your payment. Please check your dashboard for the latest status."}
            </p>
            <Link to="/dashboard">
              <Button className="rounded-full bg-paw-primary hover:bg-paw-primary-hover text-white font-manrope" data-testid="dashboard-btn">
                Go to Dashboard
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
